// src/models/post.ts
import type { Hero, RawHero } from '@/models/hero'
import { normalizeHero } from '@/models/hero'
import { normalizeTranslations, type TranslationsMap } from '@/models/translations'

export type RawWpTitle = {
  rendered: string
}

export type RawPostAcf = {
  hero?: RawHero
  excerpt?: unknown
  meta?: unknown
  components?: unknown[] | false
  settings?: Record<string, unknown>
}

export type RawPost = {
  id: number
  type: string
  slug: string
  relative_url?: string
  date?: string
  modified?: string
  title: RawWpTitle
  excerpt?: RawWpTitle
  featured_media?: number
  meta?: unknown
  seo?: unknown
  acf?: RawPostAcf
  translations?: unknown

  // WP standard taxonomies
  categories?: number[]
  tags?: number[]

  // Custom taxonomies may live directly here
  [key: string]: unknown
}

export type PostTaxonomies = Record<string, number[]>

export type PostModel = {
  id: number
  type: string
  slug: string
  relativeUrl?: string
  date?: string
  modified?: string
  title: string
  media?: number
  translations?: TranslationsMap
  hero?: Hero
  excerpt?: RawPostAcf['excerpt']
  meta?: RawPostAcf['meta']
  seo?: unknown
  components?: RawPostAcf['components']
  settings?: RawPostAcf['settings']
  taxonomies: PostTaxonomies
}

/**
 * Pick the taxonomies we care about.
 * Add custom taxonomies to the keys list.
 */
function pickTaxonomies(p: RawPost, keys: string[]): PostTaxonomies {
  const out: PostTaxonomies = {}

  for (const key of keys) {
    const val = p[key]
    if (Array.isArray(val) && val.every((x) => typeof x === 'number')) {
      out[key] = val
    }
  }

  return out
}

const post = (p: RawPost): PostModel => {
  const taxonomyKeys = ['categories'] // + 'tax_topic', 'tax_theme', ...

  return {
    id: p.id,
    type: p.type,
    slug: p.slug,
    relativeUrl: p.relative_url,
    date: p.date,
    modified: p.modified,
    title: p.title.rendered,
    media: p.featured_media || undefined,
    translations: normalizeTranslations(p.translations),
    hero: normalizeHero(p.acf?.hero),
    excerpt: p.acf?.excerpt ?? p.excerpt,
    meta: p.acf?.meta ?? p.meta,
    seo: p.seo,
    components: p.acf?.components,
    settings: p.acf?.settings,
    taxonomies: pickTaxonomies(p, taxonomyKeys),
  }
}

export default post
